<template>
  <NavBar />
  <div class="page">
    <div class="card">
      <div class="card-header">
        <div>
          <h2 class="name" v-if="!isEditing">
            {{ userInfo.firstName }} {{ userInfo.lastName }}
          </h2>
          <div v-else>
            <input v-model="editedInfo.firstName" placeholder="First Name" />
            <input v-model="editedInfo.lastName" placeholder="Last Name" />
          </div>
        </div>
        <button
          class="edit-btn"
          :class="{ 'save-btn': isEditing }"
          @click="toggleEdit"
        >
          {{ isEditing ? "Save" : "Edit" }}
        </button>
      </div>

      <div class="info-group">
        <div class="label">University</div>
        <p class="value" v-if="!isEditing">{{ userInfo.university }}</p>
        <select
          v-else
          v-model="editedInfo.university"
          class="form-select"
        >
          <option value="">Select University...</option>
          <option v-for="college in colleges" :key="college.id" :value="college.title">
            {{ college.title }}
          </option>
        </select>
      </div>

      <div class="info-group">
        <div class="label">Major</div>
        <p class="value" v-if="!isEditing">{{ userInfo.major }}</p>
        <input v-else v-model="editedInfo.major" placeholder="Major" />
      </div>

      <div class="divider"></div>

      <div class="info-group">
        <div class="label">About Me</div>
        <p class="bio" v-if="!isEditing">{{ userInfo.bio }}</p>
        <textarea
          v-else
          v-model="editedInfo.bio"
          placeholder="Tell us about yourself"
        ></textarea>
      </div>

      <!-- NEW: Credits Section -->
      <div class="info-group">
        <div class="label">Credits</div>
        <p class="value">{{ userCredits }} hours</p>
      </div>

      <div v-if="success" class="success-message">
        Profile updated successfully!
      </div>
      <div v-if="error" class="error-message">{{ error }}</div>
    </div>
  </div>
</template>

<script>
const API_BASE = process?.env?.VUE_APP_API_BASE_URL ?? '/api';
import NavBar from "@/components/NavBar.vue";
import { getUserCredits } from '@/utils/stripe.js';

export default {
  name: "EditProfilePage",
  components: {
    NavBar
  },
  data() {
    return {
      isEditing: false,
      colleges: [],
      userInfo: {
        firstName: "",
        lastName: "",
        university: "",
        major: "",
        bio: ""
      },
      editedInfo: {},
      userCredits: 0, // NEW: Credits tracking
      success: false,
      error: null
    };
  },
  methods: {
    async toggleEdit() {
      if (this.isEditing) {
        try {
          this.error = null;
          this.success = false;
          
          const userData = {
            first_name: this.editedInfo.firstName,
            last_name: this.editedInfo.lastName,
            bio: this.editedInfo.bio,
            major: this.editedInfo.major,
            school: this.editedInfo.university
          };

          const response = await fetch(`${API_BASE}/edit-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(userData)
          });

          if (!response.ok) {
            throw new Error('Failed to update profile');
          }

          const data = await response.json();
          if (data.success) {
            this.success = true;
            this.userInfo = { ...this.editedInfo };
            this.isEditing = false;
          } else {
            throw new Error(data.message || 'Failed to update profile');
          }
        } catch (error) {
          this.error = error.message;
          console.error("Failed to save profile:", error);
        }
      } else {
        this.editedInfo = { ...this.userInfo };
        this.isEditing = true;
      }
    },

    async loadUserProfile() {
      try {
        const response = await fetch(`${API_BASE}/get-logged-in-user`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to load profile');
        }
        
        const data = await response.json();
        if (data && data.user) {
          this.userInfo = {
            firstName: data.user.first_name,
            lastName: data.user.last_name,
            university: data.user.school || "",
            major: data.user.major || "",
            bio: data.user.bio || ""
          };
          this.editedInfo = { ...this.userInfo };
        }
      } catch (error) {
        this.error = "Failed to load profile data. Please refresh the page.";
        console.error("Failed to load profile:", error);
      }
    },

    async loadUserCredits() {
      try {
        this.userCredits = await getUserCredits();
      } catch (error) {
        console.error('Failed to load user credits:', error);
        this.userCredits = 0;
      }
    },

    async loadColleges() {
      try {
        const response = await fetch(`${API_BASE}/colleges`);
        const data = await response.json();
        this.colleges = data.colleges.sort((a, b) => a.title.localeCompare(b.title));
      } catch (error) {
        console.error('Error loading colleges:', error);
      }
    }
  },
  async created() {
    await this.loadUserProfile();
    await this.loadUserCredits(); // NEW: Load credits
    await this.loadColleges();
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  background-color: #054c8d;
  padding-top: 100px;
  padding-bottom: 100px;
  margin: 0;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: system-ui, -apple-system, sans-serif;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 400px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 16px;
  background-color: white;
}

.edit-btn {
  background: #3767ae;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: white;
}

.edit-btn:hover {
  background: #305a99;
}

.save-btn {
  background: #0066cc;
}

.save-btn:hover {
  background: #0052a3;
}

.name {
  margin: 0;
  font-size: 1.5rem;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.info-group {
  margin-bottom: 12px;
}

.label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #65676b;
  margin-bottom: 4px;
}

.value {
  font-size: 1rem;
  color: #1a1a1a;
  margin: 0;
}

.divider {
  height: 1px;
  background-color: #eee;
  margin: 20px 0;
}

.bio {
  line-height: 1.5;
  color: #1a1a1a;
  margin: 0;
}

input,
textarea,
select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  box-sizing: border-box;
  margin-bottom: 8px;
}

textarea {
  min-height: 100px;
  resize: vertical;
}

.success-message {
  color: #4caf50;
  margin-top: 16px;
  text-align: center;
}

.error-message {
  color: #f44336;
  margin-top: 16px;
  text-align: center;
}
</style>